import React, { useMemo, useState } from 'react'
import { Modal, Form, Checkbox } from 'semantic-ui-react'
import { AppPrimaryButton } from 'elements'
import { FlexRowBetween } from 'components'
import { X } from 'phosphor-react'

// Dynamic modal to configure validation rules based on API response data
export const SetRuleModal = ({
    open,
    onClose,
    responseItem, // The response item with Rules array from API
    initialRule,
}) => {
    // Dynamic state based on available rules
    const [ruleValues, setRuleValues] = useState({})

    // Initialize rule values from initialRule or defaults
    React.useEffect(() => {
        if (responseItem?.Rules) {
            const newRuleValues = {}
            responseItem.Rules.forEach(rule => {
                // First, set the rule itself as enabled/disabled
                newRuleValues[rule.Code] = initialRule?.[rule.Code] || false
                if (rule.ParamSchemaJson) {
                    try {
                        const schema = JSON.parse(rule.ParamSchemaJson)
                        if (schema.params) {
                            schema.params.forEach(param => {
                                const key = `${rule.Code}_${param.name}`
                                newRuleValues[key] = initialRule?.[key] || param.default || ''
                            })
                        }
                    } catch (e) {
                        console.warn('Failed to parse ParamSchemaJson for rule:', rule.Code)
                    }
                }
            })
            setRuleValues(newRuleValues)
        }
    }, [responseItem, initialRule])

    const updateRuleValue = (ruleCode, paramName, value) => {
        const key = paramName ? `${ruleCode}_${paramName}` : ruleCode
        setRuleValues(prev => {
            const newValues = {
                ...prev,
                [key]: value,
            }

            // If this is a rule toggle and the rule only has boolean parameters,
            // sync the parameter value with the rule value
            if (!paramName && responseItem?.Rules) {
                const rule = responseItem.Rules.find(r => r.Code === ruleCode)
                if (rule?.ParamSchemaJson) {
                    try {
                        const schema = JSON.parse(rule.ParamSchemaJson)
                        if (schema.params && schema.params.length === 1 && schema.params[0].type === 'boolean') {
                            const paramKey = `${ruleCode}_${schema.params[0].name}`
                            newValues[paramKey] = value
                        }
                    } catch (e) {
                        console.warn('Failed to parse ParamSchemaJson for sync:', ruleCode)
                    }
                }
            }

            return newValues
        })
    }

    const getRuleValue = (ruleCode, paramName) => {
        const key = paramName ? `${ruleCode}_${paramName}` : ruleCode
        return ruleValues[key]
    }

    const canSubmit = useMemo(() => {
        if (!responseItem?.Rules) return true

        // Validate required parameters
        return responseItem.Rules.every(rule => {
            if (rule.ParamSchemaJson) {
                try {
                    const schema = JSON.parse(rule.ParamSchemaJson)
                    if (schema.params) {
                        return schema.params.every(param => {
                            if (param.required) {
                                const value = getRuleValue(rule.Code, param.name)
                                return value !== '' && value !== null && value !== undefined
                            }
                            return true
                        })
                    }
                } catch (e) {
                    console.warn('Failed to parse ParamSchemaJson for validation:', rule.Code)
                }
            }
            return true
        })
    }, [responseItem, ruleValues])

    const handleSubmit = () => {
        const payload = {
            ResponseType: responseItem?.DisplayText,
            Rules: ruleValues,
        }
        onClose(payload)
    }

    return (
        <Modal open={open} onClose={() => onClose(null)} size="tiny">
            <Modal.Header>
                <FlexRowBetween>
                    <span>Set Rule ({responseItem?.DisplayText})</span>
                    <X size={24} onClick={() => onClose(null)} className="pointer" />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form>
                    {responseItem?.Rules?.map((rule, index) => {
                        if (!rule.IsEnabled) return null

                        const isRuleEnabled = getRuleValue(rule.Code)

                        return (
                            <div key={rule.RuleDefId || index} style={{ marginBottom: '20px' }}>
                                {/* Rule checkbox */}
                                <Form.Field>
                                    <Checkbox
                                        label={rule.DisplayName}
                                        checked={!!isRuleEnabled}
                                        onChange={() => updateRuleValue(rule.Code, null, !isRuleEnabled)}
                                    />
                                    {rule.Description && (
                                        <div style={{
                                            marginLeft: '20px',
                                            marginTop: '5px',
                                            color: '#666',
                                            fontSize: '0.9em',
                                        }}>
                                            {rule.Description}
                                        </div>
                                    )}
                                </Form.Field>

                                {/* Show parameters only when rule is enabled */}
                                {isRuleEnabled && rule.ParamSchemaJson && (
                                    <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                                        {(() => {
                                            try {
                                                const schema = JSON.parse(rule.ParamSchemaJson)
                                                if (schema.params) {
                                                    // Group parameters by type for better UI
                                                    const numberParams = schema.params.filter(p => p.type === 'number' || p.type === 'integer')
                                                    const booleanParams = schema.params.filter(p => p.type === 'boolean')
                                                    const dateParams = schema.params.filter(p => p.type === 'date')

                                                    // If rule only has boolean parameters, don't show them as separate checkboxes
                                                    // The rule checkbox itself represents the boolean value
                                                    if (booleanParams.length === schema.params.length && numberParams.length === 0 && dateParams.length === 0) {
                                                        return null
                                                    }

                                                    return (
                                                        <div>
                                                            {/* Number/Integer parameters in a row */}
                                                            {numberParams.length > 0 && (
                                                                <Form.Group widths="equal">
                                                                    {numberParams.map((param, paramIndex) => {
                                                                        const value = getRuleValue(rule.Code, param.name)
                                                                        return (
                                                                            <Form.Field key={paramIndex}>
                                                                                <label>{param.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
                                                                                <input
                                                                                    type="number"
                                                                                    value={value || ''}
                                                                                    onChange={e => updateRuleValue(rule.Code, param.name, e.target.value)}
                                                                                    placeholder={param.name}
                                                                                    required={param.required}
                                                                                />
                                                                            </Form.Field>
                                                                        )
                                                                    })}
                                                                </Form.Group>
                                                            )}

                                                            {/* Date parameters */}
                                                            {dateParams.map((param, paramIndex) => {
                                                                const value = getRuleValue(rule.Code, param.name)
                                                                return (
                                                                    <Form.Field key={paramIndex}>
                                                                        <label>{param.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
                                                                        <input
                                                                            type="date"
                                                                            value={value || ''}
                                                                            onChange={e => updateRuleValue(rule.Code, param.name, e.target.value)}
                                                                            required={param.required}
                                                                        />
                                                                    </Form.Field>
                                                                )
                                                            })}

                                                            {/* Boolean parameters as checkboxes (only if there are other parameter types) */}
                                                            {booleanParams.map((param, paramIndex) => {
                                                                const value = getRuleValue(rule.Code, param.name)
                                                                return (
                                                                    <Form.Field key={paramIndex}>
                                                                        <Checkbox
                                                                            label={param.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                            checked={!!value}
                                                                            onChange={() => updateRuleValue(rule.Code, param.name, !value)}
                                                                        />
                                                                    </Form.Field>
                                                                )
                                                            })}
                                                        </div>
                                                    )
                                                }
                                            } catch (e) {
                                                console.warn('Failed to parse ParamSchemaJson for rule:', rule.Code)
                                            }
                                            return null
                                        })()}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <AppPrimaryButton type="button" disabled={!canSubmit} onClick={handleSubmit}>
                    Save Rule
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}

// default export removed to satisfy linter rule
